import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { JobRecord } from "@rebuild/shared/models";
import { useDispatch } from "@/redux/store";
import { useTrackedWorkerJob } from "@/hooks/queries/jobs/useTrackedWorkerJob";
import { ESTIMATE_KEYS } from "@/hooks/queries/estimates/queryKeys";
import { syncEstimateDetailFromServerToRedux } from "@/hooks/estimate/syncEstimateDetailFromServer";
import { buildLineTextByUniqueIdMap, uniqueIdsWithChangedLineText } from "./cat3LineItemDiff";

type CatSyncFailure = {
  /** Worker-reported reason, when it gave one — the failed job's `error`. */
  message?: JobRecord["error"];
};

type Cat3LineHighlightContextValue = {
  highlightedUniqueIds: ReadonlySet<string>;
  pulseHighlights: (uniqueIds: readonly string[], durationMs?: number) => void;
  beginCatSync: (beforeMap: Map<string, string>, jobId: string) => void;
  isCatSyncPending: boolean;
  /** Set when the recompute job failed; stays set until the UI acknowledges it. */
  catSyncFailure: CatSyncFailure | null;
  dismissCatSyncFailure: () => void;
};

const Cat3LineHighlightContext = createContext<Cat3LineHighlightContextValue | null>(null);

/** Snapshot of line text before the recompute, carried through the job so we can diff what changed. */
type BeforeMap = Map<string, string>;

export function Cat3LineHighlightProvider({ estimateId, children }: { estimateId?: string; children: ReactNode }) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [highlightedUniqueIds, setHighlightedUniqueIds] = useState<ReadonlySet<string>>(new Set());
  const [catSyncFailure, setCatSyncFailure] = useState<CatSyncFailure | null>(null);

  const clearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (clearTimerRef.current !== null) {
        clearTimeout(clearTimerRef.current);
      }
    };
  }, []);

  const pulseHighlights = useCallback((uniqueIds: readonly string[], durationMs = 5000) => {
    if (clearTimerRef.current !== null) {
      clearTimeout(clearTimerRef.current);
      clearTimerRef.current = null;
    }

    if (uniqueIds.length === 0) {
      setHighlightedUniqueIds(new Set());
      return;
    }

    setHighlightedUniqueIds(new Set(uniqueIds));
    clearTimerRef.current = setTimeout(() => {
      setHighlightedUniqueIds(new Set());
      clearTimerRef.current = null;
    }, durationMs);
  }, []);

  // Refetch the recomputed line items and diff against the "before" snapshot, so the caller can report
  // exactly what changed.
  const refreshAndDiff = useCallback(
    async (beforeMap: BeforeMap): Promise<string[]> => {
      if (!estimateId) return [];
      const afterRooms = await syncEstimateDetailFromServerToRedux(dispatch, estimateId, { silent: true });
      const afterMap = buildLineTextByUniqueIdMap(afterRooms);
      return uniqueIdsWithChangedLineText(beforeMap, afterMap);
    },
    [estimateId, dispatch],
  );

  const finishCatSync = useCallback(
    (beforeMap: BeforeMap) => {
      void (async () => {
        const changedUniqueIds = await refreshAndDiff(beforeMap);
        if (changedUniqueIds.length > 0) {
          toast.success(`Successfully updated ${changedUniqueIds.length} line item${changedUniqueIds.length === 1 ? "" : "s"}.`);
          pulseHighlights(changedUniqueIds);
        } else {
          toast.success("No line item descriptions needed updates.");
        }
      })();
    },
    [refreshAndDiff, pulseHighlights],
  );

  const failCatSync = useCallback(
    (_beforeMap: BeforeMap, message: JobRecord["error"]) => {
      setCatSyncFailure({ message });
      toast.error("Couldn't update line items for the new category.");
      // The worker is what persists the cat change for a CAT_SYNC job, so a failure means the server
      // still holds the old cat while our caches hold the optimistic new one. Refetch both to snap back
      // to server truth rather than leaving the UI showing a change that never landed.
      if (estimateId) {
        queryClient.invalidateQueries({ queryKey: ESTIMATE_KEYS.detailMetadata(estimateId) });
        queryClient.invalidateQueries({ queryKey: ESTIMATE_KEYS.detail(estimateId) });
      }
    },
    [estimateId, queryClient],
  );

  // Don't roll back the optimistic cat the way a real failure does; the worker may yet persist it.
  const abandonCatSync = useCallback(
    (beforeMap: BeforeMap) => {
      void (async () => {
        const changedUniqueIds = await refreshAndDiff(beforeMap);
        if (changedUniqueIds.length > 0) {
          pulseHighlights(changedUniqueIds);
        }
        toast.info("Line items are still updating. Refresh in a moment to see them.");
      })();
    },
    [refreshAndDiff, pulseHighlights],
  );

  const { track: trackCatSyncJob, isTracking: isCatSyncPending } = useTrackedWorkerJob<BeforeMap>({
    estimateId,
    onSucceeded: finishCatSync,
    onFailed: failCatSync,
    onAbandoned: abandonCatSync,
  });

  const beginCatSync = useCallback(
    (beforeMap: BeforeMap, jobId: string) => {
      setCatSyncFailure(null);
      trackCatSyncJob(jobId, beforeMap);
    },
    [trackCatSyncJob],
  );

  const dismissCatSyncFailure = useCallback(() => setCatSyncFailure(null), []);

  const value = useMemo(
    () => ({
      highlightedUniqueIds,
      pulseHighlights,
      beginCatSync,
      isCatSyncPending,
      catSyncFailure,
      dismissCatSyncFailure,
    }),
    [highlightedUniqueIds, pulseHighlights, beginCatSync, isCatSyncPending, catSyncFailure, dismissCatSyncFailure],
  );

  return <Cat3LineHighlightContext.Provider value={value}>{children}</Cat3LineHighlightContext.Provider>;
}

export function useCat3LineHighlightOptional(): Cat3LineHighlightContextValue | null {
  return useContext(Cat3LineHighlightContext);
}

export function useCat3LineRowHighlight(uniqueId: string): boolean {
  const ctx = useContext(Cat3LineHighlightContext);
  return ctx?.highlightedUniqueIds.has(uniqueId) ?? false;
}

/** Whether a cat-change line-item recompute is currently running on the worker. */
export function useCatSyncPending(): boolean {
  const ctx = useContext(Cat3LineHighlightContext);
  return ctx?.isCatSyncPending ?? false;
}
